import { Modal, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  VehicleMakeCreateInput,
  VehicleMakeUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { VehicleMakeDTO } from "../api/client";
import {
  useCatalogVehicleMakesQuery,
  useCreateVehicleMake,
  useDeleteVehicleMake,
  useUpdateVehicleMake,
} from "../api/queries";
import { VehicleMakeForm } from "../ui/VehicleMakeForm";
import { VehicleMakesFilter } from "../ui/VehicleMakesFilter";
import { VehicleMakesTable } from "../ui/VehicleMakesTable";
import { VehicleMakesToolbar } from "../ui/VehicleMakesToolbar";

type VehicleMakesSearch = {
  limit?: number;
  offset?: number;
  search?: string;
};

export function CatalogVehiclesPage() {
  const nav = useNavigate({ from: "/app/catalog/vehicles" });
  const search = useSearch({
    from: "/app/catalog/vehicles",
  }) as VehicleMakesSearch;

  const list = useCatalogVehicleMakesQuery({
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    search: search.search,
  });
  const createMake = useCreateVehicleMake();
  const updateMake = useUpdateVehicleMake();
  const deleteMake = useDeleteVehicleMake();
  const canCreate = useActionPermission(["catalog:create"]);
  const canManage = useActionPermission(
    ["catalog:update", "catalog:delete"],
    "any",
  );

  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingMake, setEditingMake] = useState<VehicleMakeDTO | null>(null);

  const data = list.data?.data ?? [];
  const meta = list.data?.meta ?? { total: 0, limit: 20, offset: 0 };
  const isEdit = !!editingMake;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            { label: "ຍີ່ຫໍ້ລົດ" },
          ]}
        />
      </Header>

      <Main>
        <VehicleMakesToolbar
          onCreate={() => {
            setEditingMake(null);
            setFormOpen(true);
          }}
          canCreate={!!canCreate}
          canImport={!!canCreate}
        />

        <Modal
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) setEditingMake(null);
          }}
          title={isEdit ? "ແກ້ໄຂຍີ່ຫໍ້" : "ເພີ່ມຍີ່ຫໍ້"}
          description={
            isEdit ? "ປັບປຸງຊື່ ຫຼື ຮູບໂລໂກ້ຍີ່ຫໍ້" : "ສ້າງຍີ່ຫໍ້ລົດໃໝ່ ແລະ ອັບໂຫຼດຮູບໂລໂກ້"
          }
          size="md"
        >
          <VehicleMakeForm
            key={editingMake?.id ?? "new"}
            initialValues={
              editingMake
                ? {
                    name: editingMake.name,
                    logoKey: editingMake.imageUrl ?? "",
                  }
                : undefined
            }
            onSubmit={async (vals) => {
              try {
                setSubmitting(true);
                if (editingMake) {
                  await updateMake.mutateAsync({
                    id: editingMake.id,
                    input: vals as VehicleMakeUpdateInput,
                  });
                  toast.success("ແກ້ໄຂຍີ່ຫໍ້ສຳເລັດ");
                } else {
                  await createMake.mutateAsync(vals as VehicleMakeCreateInput);
                  toast.success("ເພີ່ມຍີ່ຫໍ້ສຳເລັດ");
                }
                setFormOpen(false);
                setEditingMake(null);
                await list.refetch();
              } catch (err: unknown) {
                toast.error(
                  (err as { message?: string })?.message ?? "ບັນທຶກບໍ່ສຳເລັດ",
                );
              } finally {
                setSubmitting(false);
              }
            }}
            onCancel={() => setFormOpen(false)}
            submitting={submitting}
          />
        </Modal>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <VehicleMakesFilter />

          <VehicleMakesTable
            canManage={!!canManage}
            isLoading={list.isLoading}
            data={data}
            offset={meta.offset}
            limit={meta.limit}
            totalCount={meta.total}
            onEdit={(row) => {
              setEditingMake(row);
              setFormOpen(true);
            }}
            onDelete={async (id) => {
              try {
                await deleteMake.mutateAsync(id);
                toast.success("ລຶບຍີ່ຫໍ້ສຳເລັດ");
                await list.refetch();
              } catch (err: unknown) {
                const msg =
                  err && typeof err === "object" && "message" in err
                    ? String((err as { message: string }).message)
                    : "ມີຮຸ່ນອ້າງອີງຢູ່ ບໍ່ສາມາດລຶບໄດ້";
                toast.error(msg);
              }
            }}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
          />
        </div>
      </Main>
    </>
  );
}
